import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useState } from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Loading } from 'src/components/Loading';
import { Modal } from 'src/components/Modal';
import { PromptTextArea } from 'src/components/PromptTextArea';
import { Textarea } from 'src/components/Textarea';
import { POST_CALL_VARIABLES } from 'src/constants';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { Prompt } from 'src/interfaces/prompt.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { ProviderSelect } from 'src/pages/Automations/components/ui/ProviderSelect';
import { CRM_PROVIDER_OPTIONS } from 'src/pages/Automations/components/wizard/WizardContext';
import { cn } from 'src/utils/className';
import { extractVariables } from 'src/utils/extractVariables';

const PROMPT_STREAM_ENDPOINT = `${process.env.REACT_APP_BASE_API_URL}/prompt-builder/chat/stream`;

type PostCallPromptSectionProps = {
  agent: Agent;
  setAgent: (value: React.SetStateAction<Agent>) => void;
  loading?: boolean;
};
export const PostCallPromptSection = ({
  agent,
  setAgent,
  loading = false,
}: PostCallPromptSectionProps) => {
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [promptGenerating, setPromptGenerating] = useState(false);
  const [promptStreaming, setPromptStreaming] = useState(false);
  const [generatePromptModalOpen, setGeneratePromptModalOpen] = useState(false);
  const [promptGeneratorInput, setPromptGeneratorInput] = useState('');
  const [token, setToken] = useState<string | undefined>();

  const { get } = useAuthedRequest();

  const { environment } = useEnvironment();
  const environmentId = environment?.envId;
  const notification = useNotification();

  const searchPrompts = useCallback(
    async (content: string) => {
      const queryObject: QueryObject = {
        filters: JSON.stringify({
          ...(content.length !== 0 && { content }),
          type: 'prompt',
          scope: 'global|user',
        }),
      };
      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data = await get(`/prompts?${query.toString()}`);

      return data.items.map((prompt: Prompt) => ({
        value: prompt.id,
        label: prompt.content,
      }));
    },
    [get],
  );

  useEffect(() => {
    async function fetchToken() {
      const token = await getAuth().currentUser?.getIdToken();
      setToken(token);
    }

    fetchToken();
  }, []);

  const populatePromptContent = async (promptId: string) => {
    if (!promptId) return;

    setLoadingPrompt(true);

    try {
      const post_call_prompt = await get(`/prompts/${promptId}`);
      setAgent((prevAgent) => ({
        ...prevAgent,
        post_call_prompt: post_call_prompt.content,
      }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingPrompt(false);
    }
  };

  return (
    <div className={cn(loading && 'opacity-50')}>
      <h4>Analyze Call</h4>

      <div className="w-full mt-2">
        <AutoCompleteSelectBox
          fetchOptions={searchPrompts}
          onChange={(value) => populatePromptContent(value)}
          className="w-full"
          variant="outlined"
          color="primary"
          size="medium"
          disabled={loading || (!agent.user_id && !!agent.id)}
        />
      </div>

      <div className="mb-10 col-span-2 mt-4">
        <div className="relative">
          <PromptTextArea
            value={agent.post_call_prompt ?? ''}
            onChange={(e) =>
              setAgent({ ...agent, post_call_prompt: e.target.value })
            }
            loading={
              loading || loadingPrompt || promptGenerating || promptStreaming
            }
            variables={Array.from(
              new Set([
                ...POST_CALL_VARIABLES,
                ...extractVariables(agent.post_call_prompt ?? ''),
              ]),
            )}
            placeholder={
              'Example: Summarize the key points discussed, extract contact information, generate follow-up actions, or reformat data as needed. All call fields are accessible via variables (i.e. {transcript})'
            }
            rows={12}
            autoScroll={promptStreaming}
            className={cn(
              'resize-none',
              promptStreaming && 'opacity-50',
              promptGenerating && 'opacity-50',
              loadingPrompt && 'opacity-50',
            )}
          />
          {promptGenerating && (
            <div className="absolute top-0 right-0 w-full h-full bg-neutral-0 flex flex-col justify-center items-center">
              <div className="flex flex-col justify-center items-center gap-2">
                <Loading size="16" className="overflow-hidden" />
                <p>Generating prompt...</p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 w-full mt-4">
        <div className="flex gap-1">
          <div>Write results to: </div>
        </div>
        <div>
          <ProviderSelect
            key={agent.id}
            label=""
            htmlFor="provider"
            providers={CRM_PROVIDER_OPTIONS}
            selectedProvider={{
              value:
                CRM_PROVIDER_OPTIONS.find(
                  (option) =>
                    option.value ===
                    agent?.activity_actions?.filter(
                      (action) => action.type === 'write_analysis_activity',
                    )[0]?.config?.destination_id,
                )?.value ?? '',
              label:
                CRM_PROVIDER_OPTIONS.find(
                  (option) =>
                    option.value ===
                    agent?.activity_actions?.filter(
                      (action) => action.type === 'write_analysis_activity',
                    )[0]?.config?.destination_id,
                )?.label ?? 'Select a provider',
            }}
            onChange={(e) => {
              if (!agent) return;

              setAgent({
                ...agent,
                activity_actions: [
                  {
                    type: 'write_analysis_activity',
                    config: {
                      provider_type: 'integration_app',
                      destination_id: e.value,
                    },
                  },
                ],
              });
            }}
          />
        </div>
      </div>

      <Modal
        title="Generate Agent Prompt"
        isOpen={generatePromptModalOpen}
        onClose={() => setGeneratePromptModalOpen(false)}
        actionButton={
          <Button
            onClick={async () => {
              setPromptGenerating(true);
              setGeneratePromptModalOpen(false);
              let completeMessage = '';
              const query = new URLSearchParams({
                last_message: promptGeneratorInput,
              });
              const eventSource = new EventSource(
                `${PROMPT_STREAM_ENDPOINT}?${query.toString()}`,
                {
                  // Pass auth for SSE
                  fetch: (input: RequestInfo | URL, init: RequestInit) =>
                    fetch(input, {
                      ...init,
                      headers: {
                        ...init?.headers,
                        Authorization: `Bearer ${token}`,
                        'x-env': environmentId || '',
                      },
                    }),
                },
              );
              eventSource.onmessage = (event) => {
                setPromptGenerating(false);
                const response = JSON.parse(event.data);
                if (response.type === 'completed') {
                  eventSource.close();
                  setPromptStreaming(false);
                  return;
                }
                setPromptStreaming(true);
                completeMessage += response.text;
                setAgent({ ...agent, post_call_prompt: completeMessage });
              };
              eventSource.onerror = () => {
                notification.error(
                  'Failed to generate prompt. Please wait a minute and try again.',
                );
                eventSource.close();
                setPromptStreaming(false);
                setPromptGenerating(false);
              };
              setPromptGeneratorInput('');
            }}
          >
            Generate
          </Button>
        }
      >
        <Textarea
          placeholder={`What do you want this agent to do, including its role, specific tasks, and overall objective with as much detail as possible?`}
          value={promptGeneratorInput}
          onChange={(e) => setPromptGeneratorInput(e.target.value)}
          rows={6}
          className="resize-none"
        />
      </Modal>
    </div>
  );
};
